import React, { useEffect, useMemo, useState } from 'react'
import type { GridController, CellFormat } from '../../compositor/GridController'
import { FuseGridBrandButton, type FuseGridBrandMarkVariant } from '../chrome/FuseGridBrand'
import {
  EXCEL_RIBBON_TABS,
  getCommandState,
  getSpreadsheetCommand,
  type RibbonTab,
  type SpreadsheetCommand,
  type SpreadsheetCommandId,
  type SpreadsheetCommandState,
} from '../../spreadsheet/commandRegistry'
import {
  buildRibbonColorInputActionPlan,
  buildRibbonDefaultMenuDescriptors,
  buildRibbonFormatCommandActionPlan,
  buildRibbonMenuActionPlan,
  currentRibbonFillColor,
  currentRibbonTextColor,
  type RibbonFormatActionPlan,
  type RibbonMenuDescriptor,
  toFormatCellsColorInputValue,
} from '../../spreadsheet/formatCellsPresentation'
import {
  buildRibbonStructuralCommandPlan,
  deriveRibbonStructuralSelectionKind,
} from '../../spreadsheet/rowColumnContextMenuPresentation'
import { buildRibbonControllerCommandPlan } from '../../spreadsheet/ribbonControllerCommandPresentation'
import {
  buildRibbonViewCommandContext,
  buildRibbonViewCommandPlan,
  type RibbonViewCommandId,
} from '../../spreadsheet/viewCommandPresentation'
import { getFuseGridButtonIcon } from '../icons/FuseGridButtonIcons'
import { getSupportedFontFamilyOptions } from '../../tableau/fontRegistry'
import './ExcelWorkbookChrome.css'

const FALLBACK_ICON = getFuseGridButtonIcon('settings')!
const SearchIcon = getFuseGridButtonIcon('search')!

const VISUALIZE_NARROW_PRIMARY_COMMANDS = new Set<SpreadsheetCommandId>([
  'creator-home',
  'add-visualization',
  'dashboard-actions',
  'map-background',
])

const VISUALIZE_NARROW_BREAKPOINT = 920

export type ExcelRibbonVariant = 'standard' | 'compact' | 'power'

export type ExcelCommandHandler = {
  onClick?: () => void | Promise<void>
  enabled?: boolean
  active?: boolean
  visible?: boolean
  disabledReason?: string
  menuItems?: Array<{
    id: string
    label: string
    selected?: boolean
    disabled?: boolean
    title?: string
    onClick?: () => void | Promise<void>
  }>
}

export type ExcelVisualizeCommandId = Extract<
  SpreadsheetCommandId,
  | 'creator-home'
  | 'import-workbook'
  | 'save-workbook'
  | 'delete-workbook'
  | 'creator-undo'
  | 'creator-redo'
  | 'developer-tools'
  | 'ai-panel'
  | 'map-background'
  | 'map-layers'
  | 'map-options'
  | 'add-visualization'
  | 'dashboard-actions'
>

export type ExcelVisualizeCommandHandler = ExcelCommandHandler
export type ExcelVisualizeCommandHandlers = Partial<Record<ExcelVisualizeCommandId, ExcelVisualizeCommandHandler>>

type ExcelRibbonProps = {
  controller?: GridController | null
  currentFormat?: CellFormat
  workbookName?: string
  onWorkbookNameChange?: (name: string) => void | Promise<void>
  saveState?: string
  showSaveState?: boolean
  variant?: ExcelRibbonVariant
  brandMarkVariant?: FuseGridBrandMarkVariant
  brandHref?: string
  onBrandClick?: () => void
  mockMode?: boolean
  rowCount?: number
  colCount?: number
  onColumnAction?: (action: 'insert_left' | 'insert_right' | 'clear' | 'delete', selection: { startCol: number; endCol: number }) => void | Promise<void>
  onRowAction?: (action: 'insert_above' | 'insert_below' | 'clear' | 'delete', selection: { startRow: number; endRow: number }) => void | Promise<void>
  onToggleQuery?: () => void
  onImport?: () => void
  onOpenFind?: (mode?: 'find' | 'replace') => void
  onOpenFormatCells?: () => void
  onToggleFullscreen?: () => void
  onViewStateChange?: () => void
  commandHandlers?: Partial<Record<SpreadsheetCommandId, ExcelCommandHandler>>
  visualizeActions?: ExcelVisualizeCommandHandlers
}

function commandTitle(command: SpreadsheetCommand): string {
  const shortcut = command.shortcut ? ` (${command.shortcut})` : ''
  const suffix = command.implemented ? '' : ' - coming soon'
  return `${command.label}${shortcut}${suffix}`
}

function styleSection(format: CellFormat | undefined, key: keyof CellFormat): Record<string, unknown> {
  const value = format?.[key]
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

function currentBooleanStyle(format: CellFormat | undefined, mirrorKey: keyof CellFormat, sectionKey: keyof CellFormat, sectionField: string): boolean {
  const mirror = format?.[mirrorKey]
  if (typeof mirror === 'boolean') return mirror
  const sectionValue = styleSection(format, sectionKey)[sectionField]
  return sectionValue === true
}

function defaultSiteHomeHref(): string | undefined {
  try {
    const parts = window.location.pathname.split('/').filter(Boolean)
    if (parts.length >= 3 && parts[1] === 'workbooks') {
      return `/${encodeURIComponent(parts[0])}/home`
    }
    if (parts.length >= 2 && ['home', 'explore', 'users', 'groups', 'projects', 'extracts', 'settings'].includes(parts[1] || '')) {
      return `/${encodeURIComponent(parts[0])}/home`
    }
  } catch {
    return undefined
  }
  return undefined
}

export function ExcelRibbon({
  controller,
  currentFormat,
  workbookName = 'FuseGrid Workbook',
  onWorkbookNameChange,
  saveState = 'Saved',
  showSaveState = false,
  variant = 'compact',
  brandMarkVariant = 'spark',
  brandHref,
  onBrandClick,
  mockMode = false,
  rowCount = 0,
  colCount = 0,
  onColumnAction,
  onRowAction,
  onToggleQuery,
  onImport,
  onOpenFind,
  onOpenFormatCells,
  onToggleFullscreen,
  onViewStateChange,
  commandHandlers,
  visualizeActions,
}: ExcelRibbonProps) {
  const [activeTabId, setActiveTabId] = useState<RibbonTab['id']>('home')
  const [, setViewRevision] = useState(0)
  const [workbookNameDraft, setWorkbookNameDraft] = useState(workbookName)
  const [workbookNameEditing, setWorkbookNameEditing] = useState(false)
  const [workbookNameSaving, setWorkbookNameSaving] = useState(false)
  const [openCommandMenuId, setOpenCommandMenuId] = useState<SpreadsheetCommandId | null>(null)
  const [openOverflowMenu, setOpenOverflowMenu] = useState(false)
  const [openOverflowSubmenuId, setOpenOverflowSubmenuId] = useState<SpreadsheetCommandId | null>(null)
  const [isNarrowRibbon, setIsNarrowRibbon] = useState(false)
  const activeTab = EXCEL_RIBBON_TABS.find((tab) => tab.id === activeTabId) ?? EXCEL_RIBBON_TABS[1]
  const hasSelection = Boolean(controller?.selection) || mockMode
  const selectionKind = deriveRibbonStructuralSelectionKind({
    mockMode,
    selection: controller?.selection,
    colCount: Number(colCount) || controller?.getColCount() || 0,
    rowCount: Number(rowCount) || controller?.getRowCount() || 0,
  })
  const commandOverrides = useMemo(() => {
    const out: Partial<Record<SpreadsheetCommandId, SpreadsheetCommandState>> = {}
    const entries = [
      ...Object.entries(commandHandlers ?? {}),
      ...Object.entries(visualizeActions ?? {}),
    ]
    for (const [rawId, handler] of entries) {
      const id = rawId as SpreadsheetCommandId
      const state: SpreadsheetCommandState = {
        enabled: Boolean(handler?.onClick || handler?.menuItems?.length) && handler.enabled !== false,
      }
      if (typeof handler?.active === 'boolean') state.active = handler.active
      if (typeof handler?.visible === 'boolean') state.visible = handler.visible
      if (handler?.disabledReason) state.disabledReason = handler.disabledReason
      out[id] = state
    }
    return out
  }, [commandHandlers, visualizeActions])
  const localPreviewAttached = Boolean(controller?.hasLocalQueryPreview?.())
  const currentBold = currentBooleanStyle(currentFormat, 'bold', 'font', 'bold')
  const currentItalic = currentBooleanStyle(currentFormat, 'italic', 'font', 'italic')
  const currentUnderline = Boolean(currentFormat?.underline ?? styleSection(currentFormat, 'font').underline)
  const fontFamilyOptions = useMemo(() => getSupportedFontFamilyOptions(['table', 'ui']), [])
  const viewCommandContext = buildRibbonViewCommandContext({
    mockMode,
    hasController: Boolean(controller),
    gridlinesVisible: controller?.getGridlinesVisible(),
    frozenPaneRows: controller?.getFrozenRowsTop(),
    frozenPaneCols: controller?.getFrozenColumnsLeft(),
    zoom: controller?.getZoom(),
  })
  const commandContext = {
    hasSelection,
    currentFormat,
    streamerAttached: Boolean(controller?.streamer),
    localPreviewAttached,
    selectionKind,
    hasStructuralActions: Boolean(onColumnAction || onRowAction),
    ...viewCommandContext,
    sheetProtection: mockMode ? null : controller?.getSheetProtectionProjection?.(),
    commandOverrides,
  }

  useEffect(() => {
    if (workbookNameEditing || workbookNameSaving) return
    setWorkbookNameDraft(workbookName)
  }, [workbookName, workbookNameEditing, workbookNameSaving])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const updateNarrowRibbon = () => {
      setIsNarrowRibbon(window.innerWidth <= VISUALIZE_NARROW_BREAKPOINT)
    }
    updateNarrowRibbon()
    window.addEventListener('resize', updateNarrowRibbon)
    return () => window.removeEventListener('resize', updateNarrowRibbon)
  }, [])

  useEffect(() => {
    setOpenCommandMenuId(null)
    setOpenOverflowMenu(false)
    setOpenOverflowSubmenuId(null)
  }, [activeTabId, isNarrowRibbon, variant])

  const applyFormat = (format: Partial<CellFormat>) => {
    if (!controller || mockMode) return
    controller.applyFormatToSelection(format)
  }

  const executeRibbonFormatActionPlan = (plan: RibbonFormatActionPlan | undefined): boolean => {
    if (!plan) return false
    switch (plan.action) {
      case 'apply-format':
        applyFormat(plan.format as Partial<CellFormat>)
        return true
      case 'merge-center':
        controller?.mergeSelection()
        applyFormat(plan.format as Partial<CellFormat>)
        return true
      case 'clear-formats':
        controller?.clearFormatsFromSelection?.()
        return true
      case 'apply-table-style':
        controller?.formatSelectionAsTable?.(plan.styleId)
        return true
      case 'open-format-cells':
        onOpenFormatCells?.()
        return true
      case 'autofit-column-width':
        controller?.autoSizeSelectedColumns?.()
        return true
      case 'autofit-row-height':
        controller?.autoSizeSelectedRows?.()
        return true
      case 'sort-selection':
        controller?.sortSelection(plan.descending)
        return true
      case 'apply-autofilter':
        controller?.applyAutoFilterToSelection()
        return true
      case 'clear-autofilter':
        controller?.clearAutoFilter()
        return true
      case 'merge-across':
        controller?.mergeAcrossSelection?.()
        return true
      case 'unmerge':
        controller?.unmergeSelection()
        return true
    }
  }

  const executeRibbonMenuAction = (entry: RibbonMenuDescriptor) => {
    executeRibbonFormatActionPlan(buildRibbonMenuActionPlan(entry))
  }

  const buildRibbonMenuItems = (items: RibbonMenuDescriptor[]): ExcelCommandHandler['menuItems'] => (
    items.map((entry) => ({
      id: entry.id,
      label: entry.label,
      title: entry.title,
      selected: entry.selected,
      onClick: () => executeRibbonMenuAction(entry),
    }))
  )

  const defaultMenuItemsForCommand = (id: SpreadsheetCommandId): ExcelCommandHandler['menuItems'] => {
    if (
      id === 'font-family' ||
      id === 'font-size' ||
      id === 'font-color' ||
      id === 'fill-color' ||
      id === 'number-format' ||
      id === 'borders' ||
      id === 'cell-styles' ||
      id === 'format-as-table' ||
      id === 'format-cells' ||
      id === 'sort-filter' ||
      id === 'merge-center'
    ) {
      return buildRibbonMenuItems(buildRibbonDefaultMenuDescriptors({
        id,
        currentFormat,
        fontFamilyOptions,
      }))
    }
    return []
  }

  const commitWorkbookNameDraft = () => {
    setWorkbookNameEditing(false)
    const nextName = String(workbookNameDraft || '').trim()
    const currentName = String(workbookName || '').trim() || 'FuseGrid Workbook'
    if (!onWorkbookNameChange || nextName === currentName || !nextName) {
      setWorkbookNameDraft(currentName)
      return
    }
    setWorkbookNameDraft(nextName)
    setWorkbookNameSaving(true)
    void Promise.resolve(onWorkbookNameChange(nextName))
      .catch(() => {
        setWorkbookNameDraft(currentName)
      })
      .finally(() => {
        setWorkbookNameSaving(false)
      })
  }

  const cancelWorkbookNameDraft = () => {
    setWorkbookNameEditing(false)
    setWorkbookNameDraft(String(workbookName || '').trim() || 'FuseGrid Workbook')
  }

  const notifyViewStateChanged = () => {
    setViewRevision((revision) => revision + 1)
    onViewStateChange?.()
  }

  const executeRibbonStructuralCommand = (id: 'insert-cells' | 'delete-cells') => {
    const plan = buildRibbonStructuralCommandPlan({
      commandId: id,
      selection: controller?.selection,
      selectionKind,
    })
    if (!plan) return
    if (plan.kind === 'column') {
      void Promise.resolve(onColumnAction?.(plan.action, plan.selection))
    } else {
      void Promise.resolve(onRowAction?.(plan.action, plan.selection))
    }
  }

  const executeRibbonViewCommand = (id: RibbonViewCommandId) => {
    const plan = buildRibbonViewCommandPlan(id)
    switch (plan.action) {
      case 'freeze-selection':
        controller?.freezePanesAtSelection()
        break
      case 'set-frozen-panes':
        controller?.setFrozenPanes(plan.rows, plan.cols)
        break
      case 'toggle-gridlines':
        controller?.toggleGridlinesVisible()
        break
      case 'set-zoom':
        controller?.setZoom(plan.zoom)
        break
    }
    if (plan.notifyViewStateChanged) {
      notifyViewStateChanged()
    }
  }

  const executeRibbonControllerCommand = (id: SpreadsheetCommandId): boolean => {
    const plan = buildRibbonControllerCommandPlan(id)
    if (!plan) return false
    switch (plan.action) {
      case 'undo':
        controller?.streamer?.undo()
        return true
      case 'redo':
        controller?.streamer?.redo()
        return true
      case 'perform-paste':
        void controller?.performPaste()
        return true
      case 'perform-cut':
        void controller?.performCut()
        return true
      case 'perform-copy':
        void controller?.performCopy()
        return true
      case 'activate-format-painter':
        controller?.activateFormatPainter()
        return true
      case 'start-autosum':
        controller?.startAutoSumAtSelection()
        return true
      case 'create-table':
        controller?.createTableFromSelection()
        return true
      case 'remove-duplicates':
        controller?.removeDuplicatesFromSelection()
        return true
      case 'apply-autofilter':
        controller?.applyAutoFilterToSelection()
        return true
      case 'open-find':
        onOpenFind?.(plan.mode)
        return true
      case 'import-data':
        onImport?.()
        return true
      case 'import-excel-workbook':
      case 'export-excel-workbook':
        return false
      case 'toggle-query':
        onToggleQuery?.()
        return true
      case 'toggle-fullscreen':
        onToggleFullscreen?.()
        return true
    }
  }

  const executeCommand = (id: SpreadsheetCommandId) => {
    if (mockMode) return
    const commandHandler = commandHandlers?.[id] ?? visualizeActions?.[id as ExcelVisualizeCommandId]
    if (commandHandler) {
      const handler = commandHandler
      if (handler?.enabled === false || !handler.onClick) return
      void Promise.resolve(handler.onClick())
      return
    }
    if (!controller && id !== 'connect-data' && id !== 'import-data' && id !== 'import-excel-workbook' && id !== 'export-excel-workbook' && id !== 'query-builder' && id !== 'find' && id !== 'fullscreen') return
    if (executeRibbonControllerCommand(id)) return
    if (executeRibbonFormatActionPlan(buildRibbonFormatCommandActionPlan({
      commandId: id,
      currentFormat,
      currentBold,
      currentItalic,
      currentUnderline,
    }))) return
    switch (id) {
      case 'insert-cells':
        executeRibbonStructuralCommand('insert-cells')
        return
      case 'delete-cells':
        executeRibbonStructuralCommand('delete-cells')
        return
      case 'freeze-panes':
        executeRibbonViewCommand('freeze-panes')
        return
      case 'freeze-top-row':
        executeRibbonViewCommand('freeze-top-row')
        return
      case 'freeze-first-column':
        executeRibbonViewCommand('freeze-first-column')
        return
      case 'unfreeze-panes':
        executeRibbonViewCommand('unfreeze-panes')
        return
      case 'gridlines':
        executeRibbonViewCommand('gridlines')
        return
      case 'zoom':
        executeRibbonViewCommand('zoom')
        return
      default:
        return
    }
  }

  const renderCommand = (id: SpreadsheetCommandId) => {
    const command = getSpreadsheetCommand(id)
    const state = getCommandState(command, commandContext)
    if (state.visible === false) return null
    const Icon = getFuseGridButtonIcon(command.icon) ?? FALLBACK_ICON
    const commandHandler = commandHandlers?.[id] ?? visualizeActions?.[id as ExcelVisualizeCommandId]
    const menuItems = commandHandler?.menuItems ?? defaultMenuItemsForCommand(id) ?? []
    const hasMenu = menuItems.length > 0
    const active = Boolean(state.active)
    const disabled = !state.enabled && !mockMode
    const label = command.shortLabel ?? command.label
    const showLabel = variant !== 'compact' && (
      command.label.length <= 12 ||
      command.id === 'number-format' ||
      command.id === 'query-builder' ||
      command.id === 'import-data'
    )
    const colorValue = command.swatch === 'font'
      ? toFormatCellsColorInputValue(currentRibbonTextColor(currentFormat), '#111827')
      : toFormatCellsColorInputValue(currentRibbonFillColor(currentFormat), '#fff2cc')
    const colorInputKind = command.swatch
    const swatchStyle = colorInputKind
      ? { background: colorValue }
      : undefined
    const disabledReason = commandHandler?.disabledReason ?? state.disabledReason
    const title = disabled && disabledReason
      ? `${commandTitle(command)} - ${disabledReason}`
      : commandTitle(command)
    const menuOpen = openCommandMenuId === id
    return (
      <span key={id} className="fg-excel-ribbon-command-wrap">
        <button
          type="button"
          className={[
            'fg-excel-ribbon-button',
            showLabel ? 'fg-excel-ribbon-button-wide' : '',
            active ? 'fg-excel-ribbon-button-active' : '',
          ].filter(Boolean).join(' ')}
          title={title}
          aria-label={command.label}
          aria-pressed={active || undefined}
          aria-haspopup={hasMenu ? 'menu' : undefined}
          aria-expanded={hasMenu ? menuOpen : undefined}
          disabled={disabled}
          data-command-id={id}
          data-implemented={command.implemented ? 'true' : 'false'}
          onClick={() => {
            if (hasMenu) {
              setOpenOverflowMenu(false)
              setOpenOverflowSubmenuId(null)
              setOpenCommandMenuId((current) => (current === id ? null : id))
              return
            }
            setOpenCommandMenuId(null)
            setOpenOverflowMenu(false)
            setOpenOverflowSubmenuId(null)
            executeCommand(id)
          }}
        >
          <Icon size={16} strokeWidth={2} />
          {showLabel && <span className="fg-excel-ribbon-button-label">{label}</span>}
          {colorInputKind && (
            <>
              <span className="fg-excel-command-swatch" style={swatchStyle} />
              {!mockMode && (
                <input
                  className="fg-excel-ribbon-color-input"
                  type="color"
                  value={colorValue}
                  aria-label={command.label}
                  title={title}
                  onChange={(event) => {
                    executeRibbonFormatActionPlan(buildRibbonColorInputActionPlan({
                      kind: colorInputKind,
                      color: event.target.value,
                    }))
                  }}
                  data-lpignore="true"
                  autoComplete="off"
                />
              )}
            </>
          )}
        </button>
        {hasMenu && menuOpen && !disabled && (
          <div className="fg-excel-ribbon-menu" role="menu" aria-label={command.label}>
            {menuItems.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitemradio"
                aria-checked={Boolean(item.selected)}
                className={item.selected ? 'fg-excel-ribbon-menu-item fg-excel-ribbon-menu-item-selected' : 'fg-excel-ribbon-menu-item'}
                title={item.title ?? item.label}
                disabled={item.disabled}
                data-menu-item-id={item.id}
                onClick={() => {
                  if (item.disabled || !item.onClick) return
                  setOpenCommandMenuId(null)
                  void Promise.resolve(item.onClick())
                }}
              >
                <span className="fg-excel-ribbon-menu-check">{item.selected ? '✓' : ''}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        )}
      </span>
    )
  }

  const commandStateForId = (id: SpreadsheetCommandId) => {
    const command = getSpreadsheetCommand(id)
    return {
      command,
      state: getCommandState(command, commandContext),
      handler: commandHandlers?.[id] ?? visualizeActions?.[id as ExcelVisualizeCommandId],
    }
  }

  const renderOverflowCommand = (id: SpreadsheetCommandId) => {
    const { command, state, handler } = commandStateForId(id)
    if (state.visible === false) return null
    const Icon = getFuseGridButtonIcon(command.icon) ?? FALLBACK_ICON
    const menuItems = handler?.menuItems ?? defaultMenuItemsForCommand(id) ?? []
    const hasMenu = menuItems.length > 0
    const disabled = !state.enabled && !mockMode
    const disabledReason = handler?.disabledReason ?? state.disabledReason
    const title = disabled && disabledReason
      ? `${commandTitle(command)} - ${disabledReason}`
      : commandTitle(command)
    const submenuOpen = openOverflowSubmenuId === id
    return (
      <div key={id} className="fg-excel-ribbon-overflow-item">
        <button
          type="button"
          role="menuitem"
          className={state.active ? 'fg-excel-ribbon-overflow-command fg-excel-ribbon-menu-item-selected' : 'fg-excel-ribbon-overflow-command'}
          title={title}
          aria-label={command.label}
          aria-haspopup={hasMenu ? 'menu' : undefined}
          aria-expanded={hasMenu ? submenuOpen : undefined}
          disabled={disabled}
          data-command-id={id}
          data-implemented={command.implemented ? 'true' : 'false'}
          onClick={() => {
            if (disabled) return
            if (hasMenu) {
              setOpenOverflowSubmenuId((current) => (current === id ? null : id))
              return
            }
            setOpenOverflowMenu(false)
            setOpenOverflowSubmenuId(null)
            executeCommand(id)
          }}
        >
          <Icon size={15} strokeWidth={2} />
          <span>{command.label}</span>
          {hasMenu ? <span className="fg-excel-ribbon-overflow-caret">›</span> : null}
        </button>
        {hasMenu && submenuOpen && !disabled && (
          <div className="fg-excel-ribbon-overflow-submenu" role="menu" aria-label={command.label}>
            {menuItems.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitemradio"
                aria-checked={Boolean(item.selected)}
                className={item.selected ? 'fg-excel-ribbon-menu-item fg-excel-ribbon-menu-item-selected' : 'fg-excel-ribbon-menu-item'}
                title={item.title ?? item.label}
                disabled={item.disabled}
                data-menu-item-id={item.id}
                onClick={() => {
                  if (item.disabled || !item.onClick) return
                  setOpenOverflowMenu(false)
                  setOpenOverflowSubmenuId(null)
                  void Promise.resolve(item.onClick())
                }}
              >
                <span className="fg-excel-ribbon-menu-check">{item.selected ? '✓' : ''}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  const shouldCollapseVisualize = variant === 'compact' && activeTabId === 'visualize' && isNarrowRibbon
  const renderedGroups = shouldCollapseVisualize
    ? activeTab.groups
      .map((group) => ({
        ...group,
        commands: group.commands.filter((id) => VISUALIZE_NARROW_PRIMARY_COMMANDS.has(id)),
      }))
      .filter((group) => group.commands.length > 0)
    : activeTab.groups
  const overflowCommands = shouldCollapseVisualize
    ? activeTab.groups.flatMap((group) => group.commands.filter((id) => !VISUALIZE_NARROW_PRIMARY_COMMANDS.has(id)))
    : []

  return (
    <div
      className="fg-excel-chrome fg-excel-ribbon-shell"
      data-testid="fg-excel-ribbon"
      data-variant={variant}
    >
      <div className="fg-excel-titlebar" data-testid="fg-excel-titlebar">
        <div className="fg-excel-brand-block">
          <FuseGridBrandButton
            variant={brandMarkVariant}
            showLabel={false}
            href={brandHref ?? defaultSiteHomeHref()}
            onClick={onBrandClick}
            title="Go to site home"
            aria-label="Go to site home"
          />
          {onWorkbookNameChange ? (
            <input
              className="fg-excel-workbook-title-input"
              value={workbookNameDraft}
              aria-label="Workbook name"
              title={workbookNameSaving ? 'Saving workbook name' : 'Workbook name'}
              disabled={workbookNameSaving}
              onChange={(event) => setWorkbookNameDraft(event.target.value)}
              onFocus={() => setWorkbookNameEditing(true)}
              onBlur={commitWorkbookNameDraft}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  event.currentTarget.blur()
                } else if (event.key === 'Escape') {
                  event.preventDefault()
                  cancelWorkbookNameDraft()
                  event.currentTarget.blur()
                }
              }}
              data-lpignore="true"
              autoComplete="off"
            />
          ) : (
            <span className="fg-excel-workbook-title" title={workbookName}>{workbookName}</span>
          )}
          {showSaveState ? <span className="fg-excel-save-state" title={`Save status: ${saveState}`}>{saveState}</span> : null}
        </div>
        <label className="fg-excel-search" title="Search commands">
          <SearchIcon size={15} />
          <input
            type="search"
            placeholder="Search or tell me what you want to do"
            aria-label="Search commands"
            title="Search commands"
            data-lpignore="true"
            autoComplete="off"
          />
        </label>
        <div className="fg-excel-title-actions">
          {(['comments', 'share'] as SpreadsheetCommandId[]).map((id) => {
            const command = getSpreadsheetCommand(id)
            const state = getCommandState(command, commandContext)
            const Icon = getFuseGridButtonIcon(command.icon) ?? FALLBACK_ICON
            const disabled = !state.enabled && !mockMode
            return (
              <button
                key={id}
                type="button"
                className={id === 'share' ? 'fg-excel-title-button fg-excel-title-button-primary' : 'fg-excel-title-button'}
                title={commandTitle(command)}
                aria-label={command.label}
                disabled={disabled}
                onClick={() => executeCommand(id)}
              >
                <Icon size={15} />
                {command.label}
              </button>
            )
          })}
        </div>
      </div>
      <div className="fg-excel-tabs" role="tablist" aria-label="Ribbon tabs">
        {EXCEL_RIBBON_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === activeTabId}
            className={tab.id === activeTabId ? 'fg-excel-tab fg-excel-tab-active' : 'fg-excel-tab'}
            title={`${tab.label} tab`}
            onClick={() => setActiveTabId(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="fg-excel-ribbon-content" data-testid="fg-excel-ribbon-content">
        {renderedGroups.map((group) => (
          <div key={group.id} className="fg-excel-ribbon-group" aria-label={group.label}>
            <div className="fg-excel-ribbon-group-buttons">
              {group.commands.map(renderCommand)}
            </div>
            <div className="fg-excel-ribbon-group-label">{group.label}</div>
          </div>
        ))}
        {overflowCommands.length > 0 && (
          <div className="fg-excel-ribbon-group fg-excel-ribbon-overflow-group" aria-label="More Visualize commands">
            <div className="fg-excel-ribbon-group-buttons">
              <span className="fg-excel-ribbon-command-wrap">
                <button
                  type="button"
                  className="fg-excel-ribbon-button"
                  title="More Visualize commands"
                  aria-label="More Visualize commands"
                  aria-haspopup="menu"
                  aria-expanded={openOverflowMenu}
                  data-testid="fg-excel-ribbon-overflow-button"
                  onClick={() => {
                    setOpenCommandMenuId(null)
                    setOpenOverflowSubmenuId(null)
                    setOpenOverflowMenu((current) => !current)
                  }}
                >
                  {(() => {
                    const MoreIcon = getFuseGridButtonIcon('more-horizontal') ?? FALLBACK_ICON
                    return <MoreIcon size={16} strokeWidth={2} />
                  })()}
                </button>
                {openOverflowMenu && (
                  <div
                    className="fg-excel-ribbon-menu fg-excel-ribbon-overflow-menu"
                    role="menu"
                    aria-label="More Visualize commands"
                    data-testid="fg-excel-ribbon-overflow-menu"
                  >
                    {overflowCommands.map(renderOverflowCommand)}
                  </div>
                )}
              </span>
            </div>
            <div className="fg-excel-ribbon-group-label">More</div>
          </div>
        )}
      </div>
    </div>
  )
}
